import type { Point2d, Scale2d, Rectangle2d } from ".."
import { toRadians } from "../toRadians"

export type ForeshortenScaleIsomorphicOptions = Scale2d & {
  degreesTiltedFromSurface: number
  axis: "x" | "y"
}

export const foreshortenScaleIsomorphic = ({
  w,
  h,
  degreesTiltedFromSurface,
  axis = `y`,
}: ForeshortenScaleIsomorphicOptions): Scale2d => {
  return {
    w: axis === `x` ? Math.cos(toRadians(degreesTiltedFromSurface)) * w : w,
    h: axis === `y` ? Math.cos(toRadians(degreesTiltedFromSurface)) * h : h,
  }
}

export const foreshortenPointIsomorphic = (
  {
    x,
    y,
    degreesTiltedFromSurface,
    axis = `y`,
  }: Point2d & {
    degreesTiltedFromSurface: number
    axis: "x" | "y"
  },
  origin: Point2d = { x: 0, y: 0 },
): Point2d => {
  const w = x - origin.x
  const h = y - origin.y
  const { w: wNew, h: hNew } = foreshortenScaleIsomorphic({
    w,
    h,
    degreesTiltedFromSurface,
    axis,
  })

  return {
    x: origin.x + wNew,
    y: origin.y + hNew,
  }
}

export const foreshortenBoxIsomorphic = (
  {
    x,
    y,
    w,
    h,
    r,
    degreesTiltedFromSurface,
    axis = `y`,
  }: Rectangle2d & {
    degreesTiltedFromSurface: number
    axis: "x" | "y"
  },
  origin?: Point2d,
): Rectangle2d => {
  const { w: w2, h: h2 } = foreshortenScaleIsomorphic({
    w,
    h,
    degreesTiltedFromSurface,
    axis,
  })
  const { x: x2, y: y2 } = foreshortenPointIsomorphic(
    {
      x,
      y,
      degreesTiltedFromSurface,
      axis,
    },
    origin,
  )

  return {
    x: x2,
    y: y2,
    w: w2,
    h: h2,
    r,
  }
}

export const foreshortenIsomorphic = (
  degreesTiltedFromSurface: number,
  axis: "x" | "y",
): {
  scale: (scale: Scale2d) => Scale2d
  point: (point: Point2d) => Point2d
  box: (box: Rectangle2d, origin: Point2d) => Rectangle2d
} => ({
  scale: (scale: Scale2d) =>
    foreshortenScaleIsomorphic({ ...scale, degreesTiltedFromSurface, axis }),
  point: (point: Point2d) =>
    foreshortenPointIsomorphic({ ...point, degreesTiltedFromSurface, axis }),
  box: (box: Rectangle2d) =>
    foreshortenBoxIsomorphic({ ...box, degreesTiltedFromSurface, axis }),
})
